import {
  useEffect,
  useState,
  useMemo,
  useCallback,
  useRef,
  Fragment,
} from "react";
import {
  useLocation,
  useNavigate,
  useParams,
  useSearch,
} from "@tanstack/react-router";
import { useQuery, useAction, useMutation } from "convex/react";
import { useAuthToken } from "@convex-dev/auth/react";
import {
  ArrowLeft,
  Check,
  ChevronRight,
  ChevronsUpDown,
  FileText,
  Folder as FolderIcon,
  PanelLeft,
  MessageSquare,
  X,
  Loader2,
  AlertCircle,
  AlertTriangle,
  Settings,
  Undo2,
} from "lucide-react";
import { AppLogo, SearchBar, UserMenu } from "@/components/app-shell";
import { NotificationsBell } from "@/features/notifications/notifications-bell";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverAnchor,
  PopoverContent,
} from "@/components/ui/popover";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  ResizablePanelGroup,
  ResizablePanel,
  ResizableHandle,
  usePanelRef,
} from "@/components/ui/resizable";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from "@/components/ui/tooltip";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Command,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
} from "@/components/ui/command";
import { useIsDesktop, useIsLargeDesktop } from "@/hooks/use-media-query";
import { useVisualViewportHeight } from "@/hooks/use-visual-viewport-height";

import { useProjectUIStore } from "@/features/project/stores/project-ui-store";
import { getMostRecentExistingFile } from "@/features/project/lib/recentFilesHelpers";
import { derivePostCommitUiFlags } from "@/features/project/lib/commit-status-flags";
import { FileTree } from "@/features/project/components/file-tree";
import {
  canProjectDiscardToRemote,
  canProjectPublish,
  canProjectSyncFromRemote,
  getProjectSourceLabel,
} from "@/features/project-source/source-helpers";
import {
  EditorArea,
  ReadOnlyBanner,
} from "@/features/project/components/editor-area";
import { ChatPanel } from "@/features/project/components/chat-panel";
import { TableOfContentsButton } from "@/features/project/components/table-of-contents";
import {
  DirtyStateInline,
  DirtyStateMobileBanner,
} from "@/features/project/components/dirty-state-banner";
import { ConflictResolution } from "@/features/conflict/conflict-resolution";
import { ProjectSettings } from "@/features/settings/project-settings";
import { DialogSheet } from "@/components/dialog-sheet";
import { api } from "@/lib/convex-api";
import {
  organizationRoute,
  projectFileRoute,
  projectRoute,
  projectSelectRoute,
} from "@/lib/app-routes";
import { isAiChatEnabled } from "@/lib/product-config";
import {
  formatUserFacingError,
  isLockContentionError,
} from "@/lib/errorMessages";
import { toast } from "sonner";
import type { FileNode } from "@/features/project/types";
import type { Id } from "../../../../backend/convex/_generated/dataModel";

// ---------------------------------------------------------------------------
// Helpers: build file tree from flat file list
// ---------------------------------------------------------------------------

function buildFileTree(
  files: Array<{ path: string }>,
  dirtyPathSet: Set<string>,
  deletedPathSet: Set<string>,
): FileNode[] {
  const root: FileNode[] = [];
  const folderMap = new Map<string, FileNode>();
  const existingFilePaths = new Set(files.map((file) => file.path));

  // Ensure parent folders exist
  function ensureFolder(parts: string[]): FileNode {
    const path = parts.join("/");
    const existing = folderMap.get(path);
    if (existing) return existing;

    const node: FileNode = {
      name: parts[parts.length - 1]!,
      path,
      type: "folder",
      children: [],
      isDirty: false,
      hasDirtyDescendant: false,
    };
    folderMap.set(path, node);

    if (parts.length === 1) {
      root.push(node);
    } else {
      const parent = ensureFolder(parts.slice(0, -1));
      parent.children!.push(node);
    }

    return node;
  }

  for (const file of files) {
    const parts = file.path.split("/");
    if (parts.length === 1) {
      // Root-level file
      root.push({
        name: parts[0]!,
        path: file.path,
        type: "file",
        isDirty: dirtyPathSet.has(file.path),
        hasDirtyDescendant: false,
      });
    } else {
      // File inside folder(s)
      const parentFolder = ensureFolder(parts.slice(0, -1));
      parentFolder.children!.push({
        name: parts[parts.length - 1]!,
        path: file.path,
        type: "file",
        isDirty: dirtyPathSet.has(file.path),
        hasDirtyDescendant: false,
      });
    }
  }

  for (const path of deletedPathSet) {
    if (existingFilePaths.has(path)) continue;
    const parts = path.split("/");
    if (parts.length === 1) {
      root.push({
        name: parts[0]!,
        path,
        type: "file",
        isDirty: dirtyPathSet.has(path),
        hasDirtyDescendant: false,
        isDeleted: true,
      });
    } else {
      const parentFolder = ensureFolder(parts.slice(0, -1));
      parentFolder.children!.push({
        name: parts[parts.length - 1]!,
        path,
        type: "file",
        isDirty: dirtyPathSet.has(path),
        hasDirtyDescendant: false,
        isDeleted: true,
      });
    }
  }

  function annotateFolderDirtyState(node: FileNode): boolean {
    if (node.type !== "folder") {
      return Boolean(node.isDirty);
    }
    let hasDirtyChild = false;
    const children = node.children ?? [];
    for (const child of children) {
      if (annotateFolderDirtyState(child)) {
        hasDirtyChild = true;
      }
    }
    node.hasDirtyDescendant = hasDirtyChild;
    return hasDirtyChild;
  }

  for (const rootNode of root) {
    annotateFolderDirtyState(rootNode);
  }

  return root;
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

interface ProjectViewPageProps {
  projectId: Id<"projects">;
  noFileRoutePath: typeof projectSelectRoute;
}

function buildProjectRouteParams(args: {
  orgSlug: string;
  routeProjectId: string;
  routeFilePath?: string | null;
}) {
  if (args.routeFilePath) {
    return {
      orgSlug: args.orgSlug,
      id: args.routeProjectId,
      _splat: args.routeFilePath,
    };
  }
  return {
    orgSlug: args.orgSlug,
    id: args.routeProjectId,
  };
}

function useProjectRouteState() {
  const params = useParams({ strict: false });
  const location = useLocation();
  const orgSlug = (params.orgSlug as string | undefined) ?? "";
  const routeProjectId = (params.id as string | undefined) ?? "";
  const routeFilePath = (params._splat as string | undefined) ?? null;
  const isNoFileRoute = location.pathname.endsWith("/select");
  const baseTo = routeFilePath
    ? projectFileRoute
    : isNoFileRoute
      ? projectSelectRoute
      : projectRoute;
  const baseParams = useMemo(
    () =>
      buildProjectRouteParams({
        orgSlug,
        routeProjectId,
        routeFilePath,
      }),
    [orgSlug, routeProjectId, routeFilePath],
  );
  return {
    orgSlug,
    routeProjectId,
    routeFilePath,
    isNoFileRoute,
    baseTo,
    baseParams,
    location,
  };
}

type ProjectSearch = {
  panel?: "files" | "chat" | "explorer";
  cmdk?: "1";
  modal?: "org_settings" | "user_settings" | "project_settings";
};

function mergeProjectSearch(
  prev: unknown,
  updates: Partial<ProjectSearch> = {},
): ProjectSearch {
  const next = { ...((prev as Record<string, unknown> | undefined) ?? {}) };
  delete next.file;
  return {
    ...(next as ProjectSearch),
    ...updates,
  };
}

export const ProjectViewPage = ({
  projectId,
  noFileRoutePath,
}: ProjectViewPageProps) => {
  const search = useSearch({ strict: false }) as ProjectSearch;
  const {
    orgSlug,
    routeProjectId,
    routeFilePath,
    isNoFileRoute,
    baseTo,
    baseParams,
    location,
  } = useProjectRouteState();
  const navigate = useNavigate();
  const isDesktop = useIsDesktop();
  const isLargeDesktop = useIsLargeDesktop();

  // Close sidebar sheets when switching to mobile so they don't pop open
  const prevIsDesktop = useRef(isDesktop);
  useEffect(() => {
    if (prevIsDesktop.current && !isDesktop) {
      useProjectUIStore.setState({ isFileTreeOpen: false, isChatOpen: false });
    }
    prevIsDesktop.current = isDesktop;
  }, [isDesktop]);

  const token = useAuthToken();
  const currentUser = useQuery(api.users.me);
  const selectedFilePath = useProjectUIStore((s) => s.selectedFilePath);
  const recentFiles = useProjectUIStore((s) => s.recentFiles);
  const selectFile = useProjectUIStore((s) => s.selectFile);
  const setProjectId = useProjectUIStore((s) => s.setProjectId);
  const requestDraftClear = useProjectUIStore((s) => s.requestDraftClear);
  // Load project details with installation info
  const project = useQuery(api.projects.getWithInstallation, {
    id: projectId,
  });

  // Load files for this project
  const files = useQuery(api.projectFiles.list, {
    projectId,
  }) as
    | Array<{
        id: string;
        path: string;
        lastSyncedSha?: string;
        lastCommittedSha?: string;
        updatedAt: number;
      }>
    | undefined;

  const openWorkingCopyAction = useAction(api.projectRemote.open);
  const getStatusAction = useAction(api.projectRemote.getStatus);
  const syncAction = useAction(api.projectRemote.syncFromRemote);
  const pushAction = useAction(api.projectRemote.publish);
  const scheduleDiscardAllMutation = useMutation(
    api.projectRemote.scheduleDiscardAll,
  );
  const discardFileAction = useAction(api.projectRemote.discardFile);
  const restoreDeletedFileAction = useAction(api.projectFiles.restoreDeleted);
  const resolveConflictAction = useAction(api.projectRemote.resolveConflict);
  const reconcileRemoteAction = useAction(api.projectRemote.reconcile);
  const emitSyncEventMutation = useMutation(api.syncEvents.emit);
  const getChangeRequestStatusAction = useAction(
    api.projectRemote.getChangeRequestStatus,
  );
  const listLocksAction = useAction(api.projectRemote.listLocks);
  const dirtyPaths = useQuery(api.projectFiles.dirtyPaths, {
    projectId,
  });
  const tombstones = useQuery(api.projectFiles.listTombstones, {
    projectId,
  }) as Array<{ path: string }> | undefined;

  const [isSyncing, setIsSyncing] = useState(false);
  const [isCommitting, setIsCommitting] = useState(false);
  const [isDiscarding, setIsDiscarding] = useState(false);
  const [isDiscardingFile, setIsDiscardingFile] = useState(false);
  const isDiscardingFileRef = useRef(false);
  const [isResolvingConflicts, setIsResolvingConflicts] = useState(false);
  const [syncError, setSyncError] = useState<string | null>(null);
  const [prUrl, setPrUrl] = useState<string | null>(null);
  const [lockHint, setLockHint] = useState<string | null>(null);
  const [isWaitingOnLock, setIsWaitingOnLock] = useState(false);
  const [hasTriedAutoSync, setHasTriedAutoSync] = useState(false);
  const [flags, setFlags] = useState<
    Array<
      | "SYNCED"
      | "DIRTY"
      | "OUT_OF_DATE"
      | "CONFLICTING"
      | "CHANGE_REQUEST_PENDING"
      | "INTEGRATION_BLOCKED"
      | "DIVERGED"
    >
  >([]);
  const [conflicts, setConflicts] = useState<
    Array<{
      path: string;
      kind: "CONTENT" | "PATH" | "CREATE";
      localContent?: string;
      remoteContent?: string;
      remoteDeleted: boolean;
      localDeleted: boolean;
      createdAt: number;
      updatedAt: number;
    }>
  >([]);
  const [selectedConflictPath, setSelectedConflictPath] = useState<
    string | undefined
  >(undefined);
  const discardTraceRef = useRef<{ path: string; startedAt: number } | null>(
    null,
  );
  const discardRequestSeqRef = useRef(0);
  const isCheckingStatusRef = useRef(false);
  const isApplyingUrlSelectionRef = useRef(false);
  const lastHandledUrlFileRef = useRef<string | null | undefined>(undefined);
  const prevHasPendingChangesRef = useRef(false);
  const hasLegacyFileSearchParam = useMemo(
    () => new URLSearchParams(location.searchStr).has("file"),
    [location.searchStr],
  );
  const isOutOfDate = flags.includes("OUT_OF_DATE");
  const isConflicting = flags.includes("CONFLICTING");
  const isChangeRequestPending = flags.includes("CHANGE_REQUEST_PENDING");
  const isIntegrationBlocked = flags.includes("INTEGRATION_BLOCKED");
  const canSyncFromRemote = project ? canProjectSyncFromRemote(project) : false;
  const canPublish = project ? canProjectPublish(project) : false;
  const canDiscardToRemote = project
    ? canProjectDiscardToRemote(project)
    : false;
  const wasLicensedRef = useRef<boolean | null>(null);
  const openWorkingCopySeqRef = useRef(0);
  const openWorkingCopyRetryTimeoutRef = useRef<number | null>(null);

  useEffect(() => {
    if (currentUser === undefined) return;
    if (!currentUser) return;
    const isLicensed =
      (currentUser as { isLicensed?: boolean }).isLicensed ?? true;
    const wasLicensed = wasLicensedRef.current;
    wasLicensedRef.current = isLicensed;
    if (isLicensed) return;
    if (routeProjectId) {
      if (wasLicensed === true) {
        toast.error(
          "Your account no longer has an active trial or subscription.",
        );
      }
      void navigate({
        to: "/",
        search: (prev) => ({ ...prev, billing: "inactive" }),
        replace: true,
      });
    }
  }, [currentUser, navigate, routeProjectId]);

  useEffect(() => {
    if (!hasLegacyFileSearchParam) {
      return;
    }
    void navigate({
      to: baseTo,
      params: baseParams as any,
      search: (prev) => mergeProjectSearch(prev),
      replace: true,
    });
  }, [baseParams, baseTo, hasLegacyFileSearchParam, navigate]);

  // When discard completes (pending changes clear), invalidate drafts, refresh status, clear isDiscarding.
  const pendingChangeCount = dirtyPaths?.length ?? 0;
  const hasPendingChanges = pendingChangeCount > 0;
  useEffect(() => {
    const prev = prevHasPendingChangesRef.current;
    prevHasPendingChangesRef.current = hasPendingChanges;
    if (prev && !hasPendingChanges && isDiscarding) {
      useProjectUIStore.getState().invalidateDrafts();
      setIsDiscarding(false);
      if (project?.id && token) {
        getStatusAction({
          projectId: project.id as Id<"projects">,
          token,
        })
          .then((status) => {
            setFlags(status.flags);
            setConflicts(status.conflicts);
          })
          .catch(() => {});
      }
    }
  }, [hasPendingChanges, isDiscarding, project?.id, token, getStatusAction]);

  // Safety: if we've been "discarding" for a long time, clear state and refresh status so the UI doesn't stay stuck.
  const DISCARD_STUCK_MS = 90_000;
  useEffect(() => {
    if (!isDiscarding || !project?.id || !token) return;
    const t = setTimeout(() => {
      setIsDiscarding(false);
      getStatusAction({ projectId: project.id as Id<"projects">, token })
        .then((status) => {
          setFlags(status.flags);
          setConflicts(status.conflicts);
        })
        .catch(() => {});
    }, DISCARD_STUCK_MS);
    return () => clearTimeout(t);
  }, [isDiscarding, project?.id, token, getStatusAction]);

  // Set project ID in store
  useEffect(() => {
    lastHandledUrlFileRef.current = undefined;
    setProjectId(String(projectId));
    setFlags([]);
    setConflicts([]);
    return () => setProjectId(null);
  }, [projectId, setProjectId]);

  const handleSync = useCallback(
    async (reasonOrEvent: unknown = "manual") => {
      const reason =
        typeof reasonOrEvent === "string" ? reasonOrEvent : "manual";
      if (!canSyncFromRemote || !project?.id || !token || isSyncing) return;
      setIsSyncing(true);
      setSyncError(null);
      setLockHint(null);
      setIsWaitingOnLock(false);
      try {
        const result = await syncAction({
          projectId: project.id as Id<"projects">,
          token,
        });
        setFlags(result.status.flags);
        setConflicts(result.status.conflicts);
        if (!result.status.flags.includes("CHANGE_REQUEST_PENDING")) {
          setPrUrl(null);
        } else {
          const pr = await getChangeRequestStatusAction({
            projectId: project.id as Id<"projects">,
            token,
          });
          setPrUrl(pr?.htmlUrl ?? null);
        }
      } catch (err) {
        console.error("Sync failed:", err);
        const friendlyMessage = formatUserFacingError(
          err,
          "Sync failed. Please try again.",
        );
        setSyncError(friendlyMessage);
        if (friendlyMessage.includes("already running")) {
          try {
            const locks = await listLocksAction({
              projectId: project.id as Id<"projects">,
              token,
            });
            const projectLock = locks.projectLock;
            if (projectLock) {
              const seconds = Math.max(
                0,
                Math.ceil((projectLock.expiresAt - Date.now()) / 1000),
              );
              setLockHint(`Active project lock (expires in ~${seconds}s).`);
            }
          } catch {
            // Ignore lock hint failures.
          }
        }
      } finally {
        setIsSyncing(false);
      }
    },
    [
      project,
      syncAction,
      token,
      isSyncing,
      canSyncFromRemote,
      getChangeRequestStatusAction,
      listLocksAction,
    ],
  );

  // Auto-sync on first load if no files exist yet
  useEffect(() => {
    if (
      !hasTriedAutoSync &&
      canSyncFromRemote &&
      files !== undefined &&
      files.length === 0 &&
      !isSyncing
    ) {
      setHasTriedAutoSync(true);
      handleSync("auto_empty_project");
    }
  }, [canSyncFromRemote, files, hasTriedAutoSync, isSyncing, handleSync]);

  const refreshStatus = useCallback(
    async (useReconcile: boolean) => {
      if (!project?.id || !token || isCheckingStatusRef.current) return;
      isCheckingStatusRef.current = true;
      try {
        const result = useReconcile
          ? canSyncFromRemote
            ? await reconcileRemoteAction({
                projectId: project.id as Id<"projects">,
                token,
              })
            : await getStatusAction({
                projectId: project.id as Id<"projects">,
                token,
              })
          : await getStatusAction({
              projectId: project.id as Id<"projects">,
              token,
            });
        setFlags(result.flags);
        setConflicts(result.conflicts);
        if (!result.flags.includes("CHANGE_REQUEST_PENDING")) {
          setPrUrl(null);
        } else {
          const pr = await getChangeRequestStatusAction({
            projectId: project.id as Id<"projects">,
            token,
          });
          setPrUrl(pr?.htmlUrl ?? null);
        }
        if (
          result.flags.includes("OUT_OF_DATE") &&
          !result.flags.includes("DIRTY") &&
          !result.flags.includes("CONFLICTING")
        ) {
          void handleSync("auto_out_of_date");
        }
      } catch (err) {
        console.error("Status refresh failed:", err);
      } finally {
        isCheckingStatusRef.current = false;
      }
    },
    [
      project,
      token,
      canSyncFromRemote,
      reconcileRemoteAction,
      getStatusAction,
      getChangeRequestStatusAction,
      handleSync,
    ],
  );

  useEffect(() => {
    const hasActiveOperation =
      isSyncing ||
      isCommitting ||
      isDiscarding ||
      isDiscardingFile ||
      isDiscardingFileRef.current ||
      isResolvingConflicts;
    const shouldRunInitialAutoSyncFirst =
      canSyncFromRemote &&
      files !== undefined &&
      files.length === 0 &&
      !hasTriedAutoSync;
    if (!project?.id || !token || hasActiveOperation) {
      return;
    }
    if (shouldRunInitialAutoSyncFirst) {
      return;
    }
    // Cancel any in-flight retry loop from a prior render.
    openWorkingCopySeqRef.current += 1;
    const mySeq = openWorkingCopySeqRef.current;
    if (openWorkingCopyRetryTimeoutRef.current !== null) {
      window.clearTimeout(openWorkingCopyRetryTimeoutRef.current);
      openWorkingCopyRetryTimeoutRef.current = null;
    }

    const startedAt = Date.now();
    const retryDelaysMs = [250, 500, 1000, 2000, 3000, 5000];
    const maxWaitMs = 60_000;

    const attemptOpenWorkingCopy = (attempt: number) => {
      void openWorkingCopyAction({
        projectId: project.id as Id<"projects">,
        token,
      })
        .then((result) => {
          if (openWorkingCopySeqRef.current !== mySeq) return;
          setIsWaitingOnLock(false);
          setLockHint(null);
          setSyncError(null);

          setFlags(result.flags);
          setConflicts(result.conflicts);
          if (
            result.flags.includes("OUT_OF_DATE") &&
            !result.flags.includes("DIRTY") &&
            !result.flags.includes("CONFLICTING")
          ) {
            void handleSync("open_working_copy_out_of_date");
          }
          if (!result.flags.includes("CHANGE_REQUEST_PENDING")) {
            setPrUrl(null);
            return;
          }
          return getChangeRequestStatusAction({
            projectId: project.id as Id<"projects">,
            token,
          }).then((pr) => setPrUrl(pr?.htmlUrl ?? null));
        })
        .catch(async (err) => {
          if (openWorkingCopySeqRef.current !== mySeq) return;

          if (isLockContentionError(err)) {
            setSyncError(null);
            setIsWaitingOnLock(true);

            // Only fetch the lock hint on the first contention to avoid spamming diagnostics.
            if (attempt === 0) {
              try {
                const locks = await listLocksAction({
                  projectId: project.id as Id<"projects">,
                  token,
                });
                if (openWorkingCopySeqRef.current !== mySeq) return;
                const projectLock = locks.projectLock;
                if (projectLock) {
                  const seconds = Math.max(
                    0,
                    Math.ceil((projectLock.expiresAt - Date.now()) / 1000),
                  );
                  setLockHint(`Active project lock (expires in ~${seconds}s).`);
                }
              } catch {
                // Ignore lock hint failures.
              }
            }

            const delayMs =
              retryDelaysMs[Math.min(attempt, retryDelaysMs.length - 1)]!;
            const elapsedMs = Date.now() - startedAt;
            if (elapsedMs + delayMs > maxWaitMs) {
              setIsWaitingOnLock(false);
              setSyncError(
                "Another sync operation is already running. Please try again in a moment.",
              );
              return;
            }

            openWorkingCopyRetryTimeoutRef.current = window.setTimeout(() => {
              attemptOpenWorkingCopy(attempt + 1);
            }, delayMs);
            return;
          }

          setIsWaitingOnLock(false);
          console.error("openWorkingCopy failed:", err);
          setSyncError(
            formatUserFacingError(
              err,
              "Failed to load project. Please refresh and try again.",
            ),
          );
        });
    };

    attemptOpenWorkingCopy(0);

    const interval = window.setInterval(() => {
      if (isSyncing || isCommitting || isDiscarding || isResolvingConflicts)
        return;
      void refreshStatus(true);
    }, 30000);

    return () => {
      window.clearInterval(interval);
      if (openWorkingCopyRetryTimeoutRef.current !== null) {
        window.clearTimeout(openWorkingCopyRetryTimeoutRef.current);
        openWorkingCopyRetryTimeoutRef.current = null;
      }
    };
  }, [
    project,
    token,
    isSyncing,
    isCommitting,
    isDiscarding,
    isDiscardingFile,
    isResolvingConflicts,
    refreshStatus,
    openWorkingCopyAction,
    getChangeRequestStatusAction,
    listLocksAction,
    files,
    hasTriedAutoSync,
    handleSync,
    canSyncFromRemote,
  ]);

  const handleCommit = useCallback(async () => {
    if (
      !canPublish ||
      !project?.id ||
      !token ||
      isCommitting ||
      isDiscarding ||
      isIntegrationBlocked
    )
      return;
    setIsCommitting(true);
    setLockHint(null);
    try {
      const now = new Date();
      const dateStamp = now.toISOString().slice(0, 10);
      const message = `docs: update project content (${dateStamp})`;
      const result = await pushAction({
        projectId: project.id as Id<"projects">,
        token,
        message,
      });
      console.log("[project-view] commit result", {
        mode: result.publish.mode,
        commitSha: result.publish.commitSha ?? null,
      });
      const nextFlags = derivePostCommitUiFlags({
        flags: result.status.flags,
        publishMode: result.publish.mode,
        commitSha: result.publish.commitSha,
      });
      if (
        result.status.flags.includes("OUT_OF_DATE") &&
        !nextFlags.includes("OUT_OF_DATE")
      ) {
        console.log(
          "[project-view] suppressing transient OUT_OF_DATE after push",
          {
            originalFlags: result.status.flags,
            nextFlags,
            commitSha: result.publish.commitSha ?? null,
          },
        );
      }
      setFlags(nextFlags);
      setConflicts(result.status.conflicts);

      // Re-check shortly after commit so true remote drift reappears quickly.
      window.setTimeout(() => {
        void refreshStatus(false);
      }, 1500);

      if (result.publish.mode === "noop") {
        toast.info("No changes to commit.");
        return;
      }

      if (result.publish.mode === "pull_request") {
        setPrUrl(result.publish.prUrl ?? null);
        return;
      }
    } catch (err) {
      console.error("Commit failed:", err);
      toast.error(formatUserFacingError(err, "Commit failed."));
    } finally {
      setIsCommitting(false);
    }
  }, [
    project,
    token,
    pushAction,
    refreshStatus,
    isCommitting,
    isDiscarding,
    isIntegrationBlocked,
    canPublish,
  ]);

  const handleDiscardAll = useCallback(async () => {
    if (
      !canDiscardToRemote ||
      !project?.id ||
      !token ||
      isDiscarding ||
      isCommitting
    ) {
      return;
    }
    setIsDiscarding(true);
    setSyncError(null);
    setLockHint(null);
    try {
      await scheduleDiscardAllMutation({
        projectId: project.id as Id<"projects">,
        token,
      });
      // Mutation completed; discardAllWithUserId is scheduled and runs with the authenticated userId.
      // isDiscarding is cleared when hasPendingChanges becomes false (useEffect) or by the 90s safety timeout.
    } catch (err) {
      console.error("Discard all failed:", err);
      toast.error(formatUserFacingError(err, "Discard failed."));
      setIsDiscarding(false);
    }
  }, [
    project,
    token,
    isDiscarding,
    isCommitting,
    scheduleDiscardAllMutation,
    canDiscardToRemote,
  ]);

  const handleDiscardFile = useCallback(
    async (path: string) => {
      if (!canDiscardToRemote || !project?.id || !token) return;
      isDiscardingFileRef.current = true;
      setIsDiscardingFile(true);
      const startedAt = Date.now();
      const startPerfMs = performance.now();
      const requestSeq = ++discardRequestSeqRef.current;
      discardTraceRef.current = { path, startedAt };
      requestDraftClear(path);
      try {
        const result = await discardFileAction({
          projectId: project.id as Id<"projects">,
          token,
          path,
        });
        setFlags(result.status.flags);
        setConflicts(result.status.conflicts);
        void startedAt;
        void startPerfMs;
        void requestSeq;
      } catch (err) {
        console.error("Discard file failed:", err);
        toast.error(formatUserFacingError(err, "Discard failed."));
      } finally {
        isDiscardingFileRef.current = false;
        setIsDiscardingFile(false);
      }
    },
    [project, token, discardFileAction, requestDraftClear, canDiscardToRemote],
  );

  useEffect(() => {
    const trace = discardTraceRef.current;
    if (!trace) return;
    const pathStillDirty = (dirtyPaths ?? []).includes(trace.path);
    void pathStillDirty;
  }, [dirtyPaths]);

  useEffect(() => {
    const trace = discardTraceRef.current;
    if (!trace) return;
  }, [hasPendingChanges, pendingChangeCount]);

  useEffect(() => {
    const trace = discardTraceRef.current;
    if (!trace) return;
    const pathStillDirty = (dirtyPaths ?? []).includes(trace.path);
    if (pathStillDirty || hasPendingChanges) return;
    discardTraceRef.current = null;
  }, [dirtyPaths, hasPendingChanges]);

  const handleUndeleteFile = useCallback(
    async (path: string) => {
      if (!project?.id || !token) return;
      try {
        const result = await restoreDeletedFileAction({
          projectId: project.id as Id<"projects">,
          token,
          path,
        });
        setFlags(result.status.flags);
        setConflicts(result.status.conflicts);
        if (!result.restored) {
          toast.error("File is no longer available to restore.");
          return;
        }
      } catch (err) {
        console.error("Restore file failed:", err);
        toast.error(formatUserFacingError(err, "Restore failed."));
      }
    },
    [project, token, restoreDeletedFileAction],
  );

  const emitDebugUiEvent = useCallback(
    (detail: string) => {
      if (!project?.id || !currentUser?.id) return;
      void emitSyncEventMutation({
        projectId: project.id as Id<"projects">,
        userId: currentUser.id,
        type: "LOCK_BUSY",
        detail: `DEBUG_UI:${detail}`,
      }).catch(() => {});
    },
    [currentUser?.id, emitSyncEventMutation, project?.id],
  );

  const handleResolveAllConflicts = useCallback(
    async (strategy: "keep_local" | "keep_remote") => {
      if (
        !canSyncFromRemote ||
        !project?.id ||
        !token ||
        isResolvingConflicts ||
        conflicts.length === 0
      ) {
        return;
      }
      setIsResolvingConflicts(true);
      setSyncError(null);
      setLockHint(null);
      emitDebugUiEvent(
        `resolveAll:start:${strategy}:count=${conflicts.length}`,
      );
      try {
        for (const conflict of conflicts) {
          const result = await resolveConflictAction({
            projectId: project.id as Id<"projects">,
            token,
            path: conflict.path,
            strategy,
          });
          emitDebugUiEvent(
            `resolveAll:result:${strategy}:path=${conflict.path}:applied=${result.applied}:flags=${result.status.flags.join(",")}:conflicts=${result.status.conflicts.length}`,
          );
          setFlags(result.status.flags);
          setConflicts(result.status.conflicts);
        }
        toast.success(
          strategy === "keep_remote"
            ? "All conflicts were resolved using the latest version."
            : "All conflicts were resolved using your version.",
        );
      } catch (err) {
        emitDebugUiEvent(
          `resolveAll:error:${strategy}:${err instanceof Error ? err.message : String(err)}`,
        );
        console.error("Resolve conflicts failed:", err);
        toast.error(formatUserFacingError(err, "Conflict resolution failed."));
      } finally {
        setIsResolvingConflicts(false);
      }
    },
    [
      project,
      token,
      isResolvingConflicts,
      conflicts,
      emitDebugUiEvent,
      resolveConflictAction,
      canSyncFromRemote,
    ],
  );

  const [showConflictView, setShowConflictView] = useState(false);
  const [settingsOpenDesktop, setSettingsOpenDesktop] = useState(false);
  const settingsOpen = isDesktop
    ? settingsOpenDesktop
    : search.modal === "project_settings";

  const setSettingsOpen = (open: boolean) => {
    if (isDesktop) {
      setSettingsOpenDesktop(open);
      return;
    }
    void navigate({
      to: baseTo,
      params: baseParams as any,
      search: (prev) =>
        mergeProjectSearch(prev, {
          modal: open ? "project_settings" : undefined,
        }),
      replace: !open,
    });
  };

  const handleResolveFile = useCallback(
    async (
      path: string,
      params: {
        strategy: "keep_local" | "keep_remote";
        mergedContent?: string;
      },
    ) => {
      if (!canSyncFromRemote || !project?.id || !token) return;
      setIsResolvingConflicts(true);
      try {
        const result = await resolveConflictAction({
          projectId: project.id as Id<"projects">,
          token,
          path,
          strategy: params.strategy,
          mergedContent: params.mergedContent,
        });
        setFlags(result.status.flags);
        setConflicts(result.status.conflicts);
      } finally {
        setIsResolvingConflicts(false);
      }
    },
    [project, token, resolveConflictAction, canSyncFromRemote],
  );

  const handleConflictResolutionComplete = useCallback(() => {
    setShowConflictView(false);
    setSelectedConflictPath(undefined);
  }, []);

  // Close conflict view when no more conflicts
  useEffect(() => {
    if (showConflictView && conflicts.length === 0) {
      setShowConflictView(false);
      setSelectedConflictPath(undefined);
    }
  }, [showConflictView, conflicts.length]);

  const deletedConflictPathSet = useMemo(
    () => new Set(conflicts.filter((c) => c.localDeleted).map((c) => c.path)),
    [conflicts],
  );
  const tombstonePathSet = useMemo(
    () => new Set<string>((tombstones ?? []).map((row) => row.path)),
    [tombstones],
  );
  const deletedPathSet = useMemo(
    () =>
      new Set<string>([
        ...Array.from(deletedConflictPathSet),
        ...Array.from(tombstonePathSet),
      ]),
    [deletedConflictPathSet, tombstonePathSet],
  );

  const isSelectedFileDeleted = useMemo(
    () => Boolean(selectedFilePath && deletedPathSet.has(selectedFilePath)),
    [selectedFilePath, deletedPathSet],
  );

  // Build file tree from flat file list
  const fileTree = useMemo(() => {
    if (!files) return [];
    return buildFileTree(files, new Set(dirtyPaths ?? []), deletedPathSet);
  }, [files, dirtyPaths, deletedPathSet]);

  const availableFilePaths = useMemo(
    () =>
      new Set<string>([
        ...(files ?? []).map((file) => file.path),
        ...Array.from(deletedPathSet),
      ]),
    [files, deletedPathSet],
  );
  const mostRecentExistingFilePath = useMemo(
    () => getMostRecentExistingFile(recentFiles, availableFilePaths),
    [recentFiles, availableFilePaths],
  );

  // URL -> store: apply deep-linked file selection and back/forward navigation.
  useEffect(() => {
    if (files === undefined) return;
    const fileInUrl = routeFilePath;
    const currentSelectedFilePath =
      useProjectUIStore.getState().selectedFilePath;
    const didUrlFileChange = lastHandledUrlFileRef.current !== fileInUrl;
    const isMissingUrlFile =
      fileInUrl !== null && !availableFilePaths.has(fileInUrl);
    if (!didUrlFileChange && !isMissingUrlFile) {
      return;
    }
    lastHandledUrlFileRef.current = fileInUrl;
    if (fileInUrl === null) {
      if (!isNoFileRoute && mostRecentExistingFilePath) {
        if (currentSelectedFilePath !== mostRecentExistingFilePath) {
          isApplyingUrlSelectionRef.current = true;
          selectFile(mostRecentExistingFilePath);
        }
        void navigate({
          to: projectFileRoute,
          params: buildProjectRouteParams({
            orgSlug,
            routeProjectId,
            routeFilePath: mostRecentExistingFilePath,
          }) as any,
          search: (prev) => mergeProjectSearch(prev),
          replace: true,
        });
        return;
      }
      if (currentSelectedFilePath !== null) {
        isApplyingUrlSelectionRef.current = true;
        selectFile(null);
      }
      return;
    }

    if (!availableFilePaths.has(fileInUrl)) {
      if (currentSelectedFilePath !== null) {
        isApplyingUrlSelectionRef.current = true;
        selectFile(null);
      }
      void navigate({
        to: noFileRoutePath,
        params: { orgSlug, id: routeProjectId },
        search: (prev) => mergeProjectSearch(prev),
        replace: true,
      });
      return;
    }

    if (currentSelectedFilePath !== fileInUrl) {
      isApplyingUrlSelectionRef.current = true;
      selectFile(fileInUrl);
    }
  }, [
    files,
    selectFile,
    availableFilePaths,
    navigate,
    isNoFileRoute,
    mostRecentExistingFilePath,
    noFileRoutePath,
    orgSlug,
    routeProjectId,
    routeFilePath,
  ]);

  // Store -> URL: push URL state when selection changes for deep links/history.
  useEffect(() => {
    if (isApplyingUrlSelectionRef.current) {
      isApplyingUrlSelectionRef.current = false;
      return;
    }
    const currentInUrl = routeFilePath;
    if (files === undefined) {
      return;
    }
    if (selectedFilePath === null && currentInUrl !== null) {
      return;
    }
    if (currentInUrl && !availableFilePaths.has(currentInUrl)) {
      return;
    }
    if (currentInUrl === selectedFilePath) return;
    if (!selectedFilePath) return;
    void navigate({
      to: projectFileRoute,
      params: buildProjectRouteParams({
        orgSlug,
        routeProjectId,
        routeFilePath: selectedFilePath,
      }) as any,
      search: (prev) => mergeProjectSearch(prev),
    });
  }, [
    availableFilePaths,
    files,
    navigate,
    orgSlug,
    routeProjectId,
    routeFilePath,
    selectedFilePath,
  ]);

  // If the currently selected file disappears (sync/discard/delete), clear
  // selection and move to the explicit no-file route.
  useEffect(() => {
    if (files === undefined || selectedFilePath === null) return;
    if (availableFilePaths.has(selectedFilePath)) return;

    isApplyingUrlSelectionRef.current = true;
    selectFile(null);
    void navigate({
      to: noFileRoutePath,
      params: { orgSlug, id: routeProjectId },
      search: (prev) => mergeProjectSearch(prev),
      replace: true,
    });
  }, [
    files,
    selectedFilePath,
    availableFilePaths,
    selectFile,
    navigate,
    noFileRoutePath,
    orgSlug,
    routeProjectId,
  ]);

  // Loading
  if (project === undefined || files === undefined) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader2 className="size-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // Not found
  if (project === null) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-2 text-muted-foreground">
        <AlertCircle className="size-6" />
        <p className="text-sm">Project not found</p>
      </div>
    );
  }

  const layoutProps: Omit<LayoutProps, never> = {
    token,
    projectId,
    project,
    fileTree,
    dirtyPaths: dirtyPaths ?? [],
    isSyncing,
    isCommitting,
    isDiscarding,
    hasPendingChanges,
    pendingChangeCount,
    syncError,
    lockHint,
    isWaitingOnLock,
    isOutOfDate,
    isConflicting,
    conflictCount: conflicts.length,
    isChangeRequestPending,
    prUrl,
    isIntegrationBlocked,
    onSync: handleSync,
    onCommit: handleCommit,
    onDiscardAll: handleDiscardAll,
    onDiscardFile: handleDiscardFile,
    onUndeleteFile: handleUndeleteFile,
    isSelectedFileDeleted,
    onResolveAllLocal: () => handleResolveAllConflicts("keep_local"),
    onResolveAllRemote: () => handleResolveAllConflicts("keep_remote"),
    isResolvingConflicts,
    onShowConflictView: () => setShowConflictView(true),
    onOpenSettings: () => setSettingsOpen(true),
  };

  return (
    <>
      {isDesktop ? (
        <DesktopLayout
          {...layoutProps}
          chatMode={isLargeDesktop ? "sidebar" : "sheet"}
        />
      ) : (
        <MobileLayout {...layoutProps} />
      )}
      <DialogSheet
        open={showConflictView}
        onOpenChange={(open) => {
          if (!open) {
            setShowConflictView(false);
            setSelectedConflictPath(undefined);
          }
        }}
        dialogClassName="sm:max-w-[95vw] w-[95vw] h-[90vh]"
      >
        <ConflictResolution
          conflicts={conflicts}
          initialSelectedPath={selectedConflictPath}
          onResolveFile={handleResolveFile}
          onComplete={handleConflictResolutionComplete}
          onCancel={() => {
            setShowConflictView(false);
            setSelectedConflictPath(undefined);
          }}
          isResolving={isResolvingConflicts}
        />
      </DialogSheet>
      <ProjectSettings
        projectId={String(projectId)}
        open={settingsOpen}
        onOpenChange={setSettingsOpen}
        onDeleted={() => {
          void navigate({
            to: organizationRoute,
            params: { orgSlug },
          });
        }}
      />
    </>
  );
};

// ---------------------------------------------------------------------------
// Types for layout props
// ---------------------------------------------------------------------------

interface LayoutProps {
  token: string | null;
  projectId: Id<"projects"> | null;
  project: {
    name: string;
    sourceType?: "GITHUB" | "CLOUD" | "GOOGLE_DRIVE";
    installationId: number | null;
    branch: string;
    lastSyncedAt?: number;
    role?: "owner" | "editor" | "viewer" | null;
  };
  fileTree: FileNode[];
  dirtyPaths: string[];
  isSyncing: boolean;
  isCommitting: boolean;
  isDiscarding: boolean;
  hasPendingChanges: boolean;
  pendingChangeCount: number;
  syncError: string | null;
  lockHint: string | null;
  isWaitingOnLock: boolean;
  isOutOfDate: boolean;
  isConflicting: boolean;
  conflictCount: number;
  isChangeRequestPending: boolean;
  prUrl: string | null;
  isIntegrationBlocked: boolean;
  onSync: () => void;
  onCommit: () => void;
  onDiscardAll: () => void;
  onDiscardFile: (path: string) => void;
  onUndeleteFile: (path: string) => Promise<void>;
  isSelectedFileDeleted: boolean;
  onResolveAllLocal: () => void;
  onResolveAllRemote: () => void;
  isResolvingConflicts: boolean;
  onShowConflictView: () => void;
  onOpenSettings: () => void;
}

// ---------------------------------------------------------------------------
// Sync banner
// ---------------------------------------------------------------------------

const SyncBanner = ({
  isSyncing,
  syncError,
  lockHint,
  isWaitingOnLock,
  isOutOfDate,
  isConflicting,
  conflictCount,
  isChangeRequestPending,
  prUrl,
  isIntegrationBlocked,
  providerLabel,
  onSync,
  onResolveAllLocal,
  onResolveAllRemote,
  onReviewConflicts,
  isResolvingConflicts,
}: {
  isSyncing: boolean;
  syncError: string | null;
  lockHint: string | null;
  isWaitingOnLock: boolean;
  isOutOfDate: boolean;
  isConflicting: boolean;
  conflictCount: number;
  isChangeRequestPending: boolean;
  prUrl: string | null;
  isIntegrationBlocked: boolean;
  providerLabel: string;
  onSync: () => void;
  onResolveAllLocal: () => void;
  onResolveAllRemote: () => void;
  onReviewConflicts: () => void;
  isResolvingConflicts: boolean;
}) => {
  if (
    !isSyncing &&
    !syncError &&
    !isWaitingOnLock &&
    !isOutOfDate &&
    !isConflicting &&
    !isChangeRequestPending &&
    !isIntegrationBlocked
  ) {
    return null;
  }

  if (isWaitingOnLock) {
    return (
      <div className="flex items-center justify-between gap-2 border-b bg-muted/30 px-3 py-2 text-sm">
        <span className="flex items-center gap-2 text-muted-foreground">
          <Loader2 className="size-3 animate-spin" />
          Another sync operation is in progress. Waiting...
          {lockHint ? ` ${lockHint}` : ""}
        </span>
      </div>
    );
  }

  if (syncError) {
    return (
      <div className="flex items-center justify-between gap-2 border-b bg-destructive/10 px-3 py-2 text-sm">
        <span className="text-destructive">
          Sync failed: {syncError}
          {lockHint ? ` ${lockHint}` : ""}
        </span>
        <Button
          variant="outline"
          size="xs"
          onClick={onSync}
          disabled={isSyncing}
        >
          {isSyncing ? (
            <>
              <Loader2 className="size-3 animate-spin" />
              Syncing...
            </>
          ) : (
            "Retry"
          )}
        </Button>
      </div>
    );
  }

  if (isIntegrationBlocked) {
    return (
      <div className="flex items-center justify-between gap-2 border-b bg-destructive/10 px-3 py-2 text-sm">
        <span className="text-destructive">
          {providerLabel} integration is blocked. Reconnect integration to
          continue.
        </span>
      </div>
    );
  }

  if (isConflicting) {
    return (
      <div className="flex items-center justify-between gap-2 border-b bg-red-100/60 px-3 py-2 text-sm dark:bg-red-500/10">
        <span className="flex items-center gap-2 text-red-900 dark:text-red-300">
          <AlertTriangle className="size-4 shrink-0" />
          {conflictCount} conflict{conflictCount === 1 ? "" : "s"} require
          resolution.
        </span>
        <div className="flex items-center gap-1">
          <Button
            variant="default"
            size="xs"
            onClick={onReviewConflicts}
            disabled={isResolvingConflicts}
          >
            Review Conflicts
          </Button>
          <Button
            variant="outline"
            size="xs"
            onClick={onResolveAllLocal}
            disabled={isResolvingConflicts}
          >
            Keep All Mine
          </Button>
          <Button
            variant="outline"
            size="xs"
            onClick={onResolveAllRemote}
            disabled={isResolvingConflicts}
          >
            Keep All Theirs
          </Button>
        </div>
      </div>
    );
  }

  if (isOutOfDate) {
    return (
      <div className="flex items-center justify-between gap-2 border-b bg-amber-100/60 px-3 py-2 text-sm dark:bg-amber-500/10">
        <span className="text-amber-900 dark:text-amber-300">
          Files changed in {providerLabel}. Sync to update your working copy.
        </span>
        <Button
          variant="outline"
          size="xs"
          onClick={onSync}
          disabled={isSyncing}
        >
          {isSyncing ? (
            <>
              <Loader2 className="size-3 animate-spin" />
              Syncing...
            </>
          ) : (
            "Sync now"
          )}
        </Button>
      </div>
    );
  }

  if (isChangeRequestPending) {
    return (
      <div className="flex items-center justify-between gap-2 border-b bg-blue-100/60 px-3 py-2 text-sm dark:bg-blue-500/10">
        <span className="text-blue-900 dark:text-blue-300">
          Publish is pending in a change request for this project.
        </span>
        {prUrl ? (
          <Button variant="outline" size="xs" asChild>
            <a href={prUrl} target="_blank" rel="noreferrer">
              Open PR
            </a>
          </Button>
        ) : null}
      </div>
    );
  }

  // When only isSyncing is true (user clicked sync), show nothing — sync feedback is on the sync button
  return null;
};

// ---------------------------------------------------------------------------
// Unified project header
// ---------------------------------------------------------------------------

const ProjectPageHeader = ({
  project,
  isDesktop,
  isSyncing,
  isCommitting,
  isDiscarding,
  hasPendingChanges,
  pendingChangeCount,
  isOutOfDate,
  isConflicting,
  isIntegrationBlocked,
  isResolvingConflicts,
  onCommit,
  onDiscardAll,
  onOpenSettings,
}: {
  project: LayoutProps["project"];
  isDesktop: boolean;
  isSyncing: boolean;
  isCommitting: boolean;
  isDiscarding: boolean;
  hasPendingChanges: boolean;
  pendingChangeCount: number;
  isOutOfDate: boolean;
  isConflicting: boolean;
  isIntegrationBlocked: boolean;
  isResolvingConflicts: boolean;
  onCommit: () => void;
  onDiscardAll: () => void;
  onOpenSettings: () => void;
}) => {
  const toggleFileTree = useProjectUIStore((s) => s.toggleFileTree);
  const isFileTreeOpen = useProjectUIStore((s) => s.isFileTreeOpen);
  const setFileTreeOpen = useProjectUIStore((s) => s.setFileTreeOpen);
  const setChatOpen = useProjectUIStore((s) => s.setChatOpen);
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as ProjectSearch;
  const { orgSlug, routeProjectId, baseTo, baseParams } =
    useProjectRouteState();
  const allProjects = useQuery(api.projects.listForUser, {}) as
    | Array<{
        id: string;
        name: string;
        slug?: string;
        organizationSlug?: string;
      }>
    | undefined;
  const canPublish = canProjectPublish(project);
  const canDiscardToRemote = canProjectDiscardToRemote(project);

  const handleToggleFileTree = () => {
    if (isDesktop) {
      toggleFileTree();
      return;
    }

    if (search.panel === "files" || isFileTreeOpen) {
      setFileTreeOpen(false);
      void navigate({
        to: baseTo,
        params: baseParams as any,
        search: (prev) => mergeProjectSearch(prev, { panel: undefined }),
        replace: true,
      });
      return;
    }

    setChatOpen(false);
    setFileTreeOpen(true);
    void navigate({
      to: baseTo,
      params: baseParams as any,
      search: (prev) => mergeProjectSearch(prev, { panel: "files" }),
    });
  };

  return (
    <header className="flex h-12 shrink-0 items-center gap-1 border-b px-3 md:gap-2">
      {/* Left: sidebar toggle */}
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            size="icon-xs"
            onClick={handleToggleFileTree}
            aria-label="Toggle file tree"
          >
            <PanelLeft className="size-4" />
          </Button>
        </TooltipTrigger>
        <TooltipContent>Toggle file tree</TooltipContent>
      </Tooltip>

      {/* Logo + project switcher + settings (owners only) */}
      <div className="flex min-w-0 items-center gap-1.5">
        <Tooltip>
          <TooltipTrigger asChild>
            <AppLogo
              compact
              to={organizationRoute}
              params={{ orgSlug }}
              aria-label="Back to dashboard"
            />
          </TooltipTrigger>
          <TooltipContent>Back to dashboard</TooltipContent>
        </Tooltip>
        <span className="text-muted-foreground/40 text-sm">›</span>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="secondary"
              size="sm"
              className="h-7 max-w-48 gap-1.5 px-2"
            >
              <span className="truncate text-sm font-medium">
                {project.name}
              </span>
              <ChevronsUpDown className="size-3.5 shrink-0 text-muted-foreground" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-64">
            <DropdownMenuLabel>{project.name}</DropdownMenuLabel>
            {project.role === "owner" && (
              <DropdownMenuItem onClick={onOpenSettings}>
                <Settings />
                Project settings
              </DropdownMenuItem>
            )}
            <DropdownMenuSeparator />
            <DropdownMenuLabel>Other projects</DropdownMenuLabel>
            {(allProjects ?? [])
              .filter((p) => p.slug !== routeProjectId)
              .map((p) => (
                <DropdownMenuItem
                  key={p.id}
                  onClick={() => {
                    if (!p.slug || !p.organizationSlug) {
                      return;
                    }
                    void navigate({
                      to: projectRoute,
                      params: { orgSlug: p.organizationSlug, id: p.slug },
                    });
                  }}
                >
                  <span className="truncate">{p.name}</span>
                </DropdownMenuItem>
              ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Center: spacer */}
      <div className="flex-1" />

      {/* Right: search + dirty banner (desktop) + user menu */}
      <div className="flex shrink-0 items-center gap-2">
        <SearchBar />
        {isDesktop && (
          <DirtyStateInline
            pendingChangeCount={pendingChangeCount}
            hasPendingChanges={hasPendingChanges}
            isCommitting={isCommitting}
            isDiscarding={isDiscarding}
            isSyncing={isSyncing}
            isOutOfDate={isOutOfDate}
            isConflicting={isConflicting}
            isIntegrationBlocked={isIntegrationBlocked}
            isResolvingConflicts={isResolvingConflicts}
            canPublish={canPublish}
            canDiscardToRemote={canDiscardToRemote}
            providerLabel={getProjectSourceLabel(project)}
            onCommit={onCommit}
            onDiscardAll={onDiscardAll}
          />
        )}
        <NotificationsBell />
        <UserMenu />
      </div>
    </header>
  );
};

// ---------------------------------------------------------------------------
// Breadcrumbs — shows path from project root above the editor
// ---------------------------------------------------------------------------

const Breadcrumbs = ({
  projectName,
  filePath,
  isDirty,
  isDeleted,
  fileTree,
  onDiscardFile,
  onUndeleteFile,
  rightSlot,
}: {
  projectName: string;
  filePath: string | null;
  isDirty?: boolean;
  isDeleted?: boolean;
  fileTree: FileNode[];
  onDiscardFile?: (path: string) => void;
  onUndeleteFile?: (path: string) => Promise<void>;
  rightSlot?: React.ReactNode;
}) => {
  const isDesktop = useIsDesktop();
  const isFileTreeOpen = useProjectUIStore((s) => s.isFileTreeOpen);
  const setFileTreeOpen = useProjectUIStore((s) => s.setFileTreeOpen);
  const search = useSearch({ strict: false }) as ProjectSearch;
  const navigate = useNavigate();
  const { orgSlug, routeProjectId, baseTo, baseParams } =
    useProjectRouteState();

  const [explorerOpen, setExplorerOpen] = useState(false);
  // The folder path the user clicked on; "" = project root
  const [explorerRoot, setExplorerRoot] = useState("");
  const [isDiscardingFile, setIsDiscardingFile] = useState(false);
  const [isUndeletingFile, setIsUndeletingFile] = useState(false);
  const viewportHeight = useVisualViewportHeight(!isDesktop && explorerOpen);

  // On mobile, drive the open state via router history so the browser back
  // button closes the explorer without navigating away.
  useEffect(() => {
    if (isDesktop) return;
    setExplorerOpen(search.panel === "explorer");
  }, [isDesktop, search.panel]);

  // Reset discarding state when file is no longer dirty
  useEffect(() => {
    if (!isDirty) setIsDiscardingFile(false);
  }, [isDirty]);
  useEffect(() => {
    if (!isDeleted) setIsUndeletingFile(false);
  }, [isDeleted]);

  if (!filePath) return null;

  const segments = filePath.split("/");

  const openExplorer = (folderPath: string) => {
    // Keep mobile overlays mutually exclusive to avoid "double close" back behavior.
    if (!isDesktop) {
      useProjectUIStore.setState({ isFileTreeOpen: false, isChatOpen: false });
      void navigate({
        to: baseTo,
        params: baseParams as any,
        search: (prev) => mergeProjectSearch(prev, { panel: "explorer" }),
      });
    }
    setExplorerRoot(folderPath);
    setExplorerOpen(true);
  };

  const handleFileSelect = (path: string) => {
    useProjectUIStore.getState().selectFile(path);
    setExplorerOpen(false);
    if (!isDesktop && search.panel === "explorer") {
      void navigate({
        to: baseTo,
        params: baseParams as any,
        search: (prev) => mergeProjectSearch(prev, { panel: undefined }),
        replace: true,
      });
    }
  };

  const breadcrumbNav = (
    <nav className="w-full mt-2 flex items-center gap-2 py-1.5 text-sm editor-max-width editor-padding-x">
      <div className="flex flex-wrap items-center gap-1 min-w-0 flex-1">
        {/* Folder icon — desktop: open sidebar if closed, else explorer; mobile: always explorer */}
        <button
          type="button"
          className="flex items-center gap-1 cursor-pointer hover:opacity-80 transition-opacity"
          onClick={() => {
            if (!isDesktop) {
              openExplorer("");
              return;
            }
            if (isFileTreeOpen) {
              openExplorer("");
            } else {
              setFileTreeOpen(true);
            }
          }}
          title={projectName}
          aria-label={`Browse files${projectName ? ` in ${projectName}` : ""}`}
        >
          <FolderIcon
            className="size-4 shrink-0 text-muted-foreground/60"
            aria-hidden
          />
          <span className="hidden sm:inline max-w-[150px] truncate align-bottom font-semibold text-foreground hover:underline">
            {projectName}
          </span>
        </button>
        {segments.map((segment, i) => {
          const isLast = i === segments.length - 1;
          // Build the folder path up to this segment
          const segPath = segments.slice(0, i + 1).join("/");
          return (
            <Fragment key={i}>
              <span className="text-muted-foreground/40">›</span>
              {isLast ? (
                <span className="inline-flex items-center gap-1.5">
                  <span
                    className="max-w-[150px] truncate align-bottom text-muted-foreground"
                    title={segment}
                  >
                    {segment}
                  </span>
                  {isDeleted && (
                    <>
                      <span className="rounded-full border border-red-300 bg-red-50 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-red-700 dark:border-red-700/70 dark:bg-red-900/20 dark:text-red-300">
                        Deleted
                      </span>
                      {onUndeleteFile && filePath && (
                        <Button
                          variant="ghost"
                          size="xs"
                          className="h-5 gap-1 px-1.5 text-[11px] text-red-700 hover:text-red-900 hover:bg-red-100 dark:text-red-300 dark:hover:text-red-100 dark:hover:bg-red-500/20"
                          disabled={isUndeletingFile}
                          onClick={() => {
                            setIsUndeletingFile(true);
                            void onUndeleteFile(filePath).finally(() => {
                              setIsUndeletingFile(false);
                            });
                          }}
                        >
                          {isUndeletingFile ? (
                            <Loader2 className="size-3 animate-spin" />
                          ) : (
                            <Undo2 className="size-3" />
                          )}
                          {isUndeletingFile ? "Undoing…" : "Undo"}
                        </Button>
                      )}
                    </>
                  )}
                </span>
              ) : (
                <button
                  type="button"
                  className="max-w-[150px] truncate align-bottom font-semibold text-muted-foreground cursor-pointer hover:underline"
                  onClick={() => openExplorer(segPath)}
                  title={segment}
                >
                  {segment}
                </button>
              )}
              {isLast && isDirty && !isDeleted && (
                <>
                  <span
                    className="relative ml-1 flex size-2 shrink-0"
                    title="Unsaved changes"
                  >
                    <span className="absolute inline-flex size-full animate-ping rounded-full bg-amber-400 opacity-75" />
                    <span className="relative inline-flex size-2 rounded-full bg-amber-500" />
                  </span>
                  {onDiscardFile && filePath && (
                    <Button
                      variant="ghost"
                      size="xs"
                      className="ml-1 h-5 gap-1 px-1.5 text-[11px] text-amber-700 hover:text-amber-900 hover:bg-amber-100 dark:text-amber-400 dark:hover:text-amber-200 dark:hover:bg-amber-500/15 max-sm:size-6 max-sm:p-1"
                      disabled={isDiscardingFile}
                      onClick={() => {
                        setIsDiscardingFile(true);
                        void Promise.resolve(onDiscardFile(filePath)).finally(
                          () => {
                            setIsDiscardingFile(false);
                          },
                        );
                      }}
                      aria-label={
                        isDiscardingFile
                          ? "Discarding changes"
                          : "Discard changes"
                      }
                    >
                      {isDiscardingFile ? (
                        <Loader2 className="size-3 animate-spin shrink-0" />
                      ) : (
                        <Undo2 className="size-3 shrink-0" />
                      )}
                      <span className="hidden sm:inline">
                        {isDiscardingFile ? "Discarding…" : "Discard Changes"}
                      </span>
                    </Button>
                  )}
                </>
              )}
            </Fragment>
          );
        })}
      </div>
      {rightSlot && <div className="shrink-0">{rightSlot}</div>}
    </nav>
  );

  /* Explorer: Popover anchored to nav on desktop, fullscreen Dialog on mobile */
  if (isDesktop) {
    return (
      <Popover open={explorerOpen} onOpenChange={setExplorerOpen}>
        <PopoverAnchor asChild>{breadcrumbNav}</PopoverAnchor>
        <PopoverContent
          align="start"
          side="bottom"
          sideOffset={4}
          className="w-72 p-0"
        >
          <BreadcrumbExplorer
            fileTree={fileTree}
            initialFolder={explorerRoot}
            projectName={projectName}
            onSelectFile={handleFileSelect}
            onClose={() => setExplorerOpen(false)}
          />
        </PopoverContent>
      </Popover>
    );
  }

  return (
    <>
      {breadcrumbNav}
      <Dialog
        open={explorerOpen}
        onOpenChange={(open) => {
          setExplorerOpen(open);
          if (!open && search.panel === "explorer") {
            void navigate({
              to: baseTo,
              params: baseParams as any,
              search: (prev) => mergeProjectSearch(prev, { panel: undefined }),
              replace: true,
            });
          }
        }}
      >
        <DialogContent
          className="h-(--visual-vh,100dvh) w-full max-w-full translate-none top-0 left-0 rounded-none border-0 p-0 sm:max-w-full"
          style={
            viewportHeight !== undefined
              ? ({
                  "--visual-vh": `${viewportHeight}px`,
                } as React.CSSProperties)
              : undefined
          }
          showCloseButton={false}
        >
          <DialogHeader className="sr-only">
            <DialogTitle>Browse files</DialogTitle>
          </DialogHeader>
          <BreadcrumbExplorer
            fileTree={fileTree}
            initialFolder={explorerRoot}
            projectName={projectName}
            onSelectFile={handleFileSelect}
            onClose={() => {
              setExplorerOpen(false);
              if (search.panel === "explorer") {
                void navigate({
                  to: baseTo,
                  params: baseParams as any,
                  search: (prev) =>
                    mergeProjectSearch(prev, { panel: undefined }),
                  replace: true,
                });
              }
            }}
            isMobile
          />
        </DialogContent>
      </Dialog>
    </>
  );
};

// ---------------------------------------------------------------------------
// BreadcrumbExplorer — drill-down file browser with search
// ---------------------------------------------------------------------------

/** Resolve a FileNode[] subtree from the root tree given a folder path like "docs/plans" */
function resolveSubtree(
  tree: FileNode[],
  folderPath: string,
): FileNode[] | null {
  if (folderPath === "") return tree;
  const parts = folderPath.split("/");
  let nodes = tree;
  for (const part of parts) {
    const match = nodes.find((n) => n.name === part && n.type === "folder");
    if (!match?.children) return null;
    nodes = match.children;
  }
  return nodes;
}

/** Recursively collect all files in a tree (for search). */
function collectFiles(nodes: FileNode[]): FileNode[] {
  const result: FileNode[] = [];
  for (const node of nodes) {
    if (node.type === "file") {
      result.push(node);
    } else if (node.children) {
      result.push(...collectFiles(node.children));
    }
  }
  return result;
}

const BreadcrumbExplorer = ({
  fileTree,
  initialFolder,
  projectName,
  onSelectFile,
  onClose,
  isMobile = false,
}: {
  fileTree: FileNode[];
  initialFolder: string;
  projectName: string;
  onSelectFile: (path: string) => void;
  onClose: () => void;
  isMobile?: boolean;
}) => {
  const [currentFolder, setCurrentFolder] = useState(initialFolder);
  const [search, setSearch] = useState("");

  // Reset when initialFolder changes (e.g. clicking a different breadcrumb segment)
  useEffect(() => {
    setCurrentFolder(initialFolder);
    setSearch("");
  }, [initialFolder]);

  const currentNodes = resolveSubtree(fileTree, currentFolder) ?? [];
  const folders = currentNodes
    .filter((n) => n.type === "folder")
    .sort((a, b) => a.name.localeCompare(b.name));
  const files = currentNodes
    .filter((n) => n.type === "file")
    .sort((a, b) => a.name.localeCompare(b.name));

  // Build breadcrumb trail for the explorer header
  const folderParts = currentFolder ? currentFolder.split("/") : [];

  // When searching, recursively find matching files
  const query = search.trim().toLowerCase();
  const isSearching = query.length > 0;
  const searchResults = useMemo(() => {
    if (!query) return [];
    const subtree = resolveSubtree(fileTree, currentFolder) ?? [];
    return collectFiles(subtree)
      .filter(
        (f) =>
          f.name.toLowerCase().includes(query) ||
          f.path.toLowerCase().includes(query),
      )
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [query, fileTree, currentFolder]);

  const navigateUp = () => {
    if (!currentFolder) return;
    const parts = currentFolder.split("/");
    parts.pop();
    setCurrentFolder(parts.join("/"));
    setSearch("");
  };

  const drillDown = (folder: FileNode) => {
    setCurrentFolder(folder.path);
    setSearch("");
  };

  return (
    <Command
      shouldFilter={false}
      className={isMobile ? "flex h-full flex-col" : "flex flex-col"}
    >
      {/* Header with back button and breadcrumb trail */}
      <div className="flex items-center gap-1.5 border-b px-3 py-2">
        {currentFolder !== "" && (
          <button
            type="button"
            onClick={navigateUp}
            className="shrink-0 rounded p-0.5 text-muted-foreground hover:bg-accent"
            aria-label="Go back"
          >
            <ArrowLeft className="size-4" />
          </button>
        )}
        <div className="flex min-w-0 flex-1 items-center gap-1 text-xs text-muted-foreground truncate">
          <button
            type="button"
            className="shrink-0 font-medium hover:underline"
            onClick={() => {
              setCurrentFolder("");
              setSearch("");
            }}
          >
            {projectName}
          </button>
          {folderParts.map((part, i) => (
            <Fragment key={i}>
              <ChevronRight className="size-3 shrink-0 opacity-40" />
              <button
                type="button"
                className="shrink-0 font-medium hover:underline truncate"
                onClick={() => {
                  setCurrentFolder(folderParts.slice(0, i + 1).join("/"));
                  setSearch("");
                }}
              >
                {part}
              </button>
            </Fragment>
          ))}
        </div>
        {isMobile && (
          <button
            type="button"
            onClick={onClose}
            className="shrink-0 rounded p-0.5 text-muted-foreground hover:bg-accent"
            aria-label="Close"
          >
            <X className="size-4" />
          </button>
        )}
      </div>

      {/* Search — uses cmdk's CommandInput for keyboard integration */}
      <CommandInput
        autoFocus
        placeholder="Search files…"
        value={search}
        onValueChange={setSearch}
      />

      {/* File/folder listing — cmdk handles arrow keys + enter */}
      <CommandList className={isMobile ? "max-h-none flex-1" : ""}>
        <CommandEmpty>No files found</CommandEmpty>

        {isSearching ? (
          <CommandGroup>
            {searchResults.map((node) => (
              <CommandItem
                key={node.path}
                value={node.path}
                onSelect={() => onSelectFile(node.path)}
              >
                <FileText className="text-muted-foreground" />
                <span className="truncate">{node.name}</span>
                <span className="ml-auto truncate text-xs text-muted-foreground/50 max-w-[40%]">
                  {node.path}
                </span>
              </CommandItem>
            ))}
          </CommandGroup>
        ) : (
          <>
            {folders.length > 0 && (
              <CommandGroup heading="Folders">
                {folders.map((folder) => (
                  <CommandItem
                    key={folder.path}
                    value={folder.path}
                    onSelect={() => drillDown(folder)}
                  >
                    <FolderIcon className="text-muted-foreground" />
                    <span className="truncate">{folder.name}</span>
                    <ChevronRight className="ml-auto size-3.5 shrink-0 text-muted-foreground/40" />
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
            {files.length > 0 && (
              <CommandGroup heading="Files">
                {files.map((file) => (
                  <CommandItem
                    key={file.path}
                    value={file.path}
                    onSelect={() => onSelectFile(file.path)}
                  >
                    <FileText className="text-muted-foreground" />
                    <span className="truncate">{file.name}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </>
        )}
      </CommandList>
    </Command>
  );
};

// ---------------------------------------------------------------------------
// Floating chat bubble
// ---------------------------------------------------------------------------

const ChatBubble = () => {
  const isDesktop = useIsDesktop();
  const isChatOpen = useProjectUIStore((s) => s.isChatOpen);
  const setChatOpen = useProjectUIStore((s) => s.setChatOpen);
  const setFileTreeOpen = useProjectUIStore((s) => s.setFileTreeOpen);
  const [keyboardInsetPx, setKeyboardInsetPx] = useState(0);
  const search = useSearch({ strict: false }) as ProjectSearch;
  const navigate = useNavigate();
  const { orgSlug, routeProjectId, baseTo, baseParams } =
    useProjectRouteState();

  useEffect(() => {
    if (isDesktop) {
      setKeyboardInsetPx(0);
      return;
    }
    const vv = window.visualViewport;
    if (!vv) {
      setKeyboardInsetPx(0);
      return;
    }
    const updateKeyboardInset = () => {
      // Keep the bubble above the mobile soft keyboard when the visual viewport shrinks.
      const inset = Math.max(0, window.innerHeight - vv.height - vv.offsetTop);
      setKeyboardInsetPx(Math.round(inset));
    };
    updateKeyboardInset();
    vv.addEventListener("resize", updateKeyboardInset);
    vv.addEventListener("scroll", updateKeyboardInset);
    window.addEventListener("orientationchange", updateKeyboardInset);
    return () => {
      vv.removeEventListener("resize", updateKeyboardInset);
      vv.removeEventListener("scroll", updateKeyboardInset);
      window.removeEventListener("orientationchange", updateKeyboardInset);
    };
  }, [isDesktop]);

  const handleToggle = () => {
    if (isDesktop) {
      setChatOpen(!isChatOpen);
      return;
    }

    if (search.panel === "chat") {
      setChatOpen(false);
      void navigate({
        to: baseTo,
        params: baseParams as any,
        search: (prev) => mergeProjectSearch(prev, { panel: undefined }),
        replace: true,
      });
      return;
    }

    setFileTreeOpen(false);
    setChatOpen(true);
    void navigate({
      to: baseTo,
      params: baseParams as any,
      search: (prev) => mergeProjectSearch(prev, { panel: "chat" }),
    });
  };

  if (!isAiChatEnabled) return null;

  return (
    <button
      onClick={handleToggle}
      className={
        isDesktop
          ? "absolute bottom-4 right-4 z-40 flex size-12 items-center justify-center rounded-full bg-foreground text-background shadow-lg transition-colors hover:bg-foreground/90"
          : "fixed bottom-4 right-4 z-40 flex size-12 items-center justify-center rounded-full bg-foreground text-background shadow-lg transition-[bottom,background-color] duration-200 hover:bg-foreground/90"
      }
      style={
        isDesktop
          ? undefined
          : {
              bottom: `calc(1rem + env(safe-area-inset-bottom) + ${keyboardInsetPx}px)`,
            }
      }
      aria-label="Toggle chat"
    >
      <MessageSquare className="size-5" />
    </button>
  );
};

// ---------------------------------------------------------------------------
// Desktop layout — resizable panels with floating chat bubble
// ---------------------------------------------------------------------------

const DesktopLayout = ({
  token,
  projectId,
  project,
  fileTree,
  dirtyPaths,
  isSyncing,
  isCommitting,
  isDiscarding,
  isResolvingConflicts,
  hasPendingChanges,
  pendingChangeCount,
  syncError,
  lockHint,
  isWaitingOnLock,
  isOutOfDate,
  isConflicting,
  conflictCount,
  isChangeRequestPending,
  prUrl,
  isIntegrationBlocked,
  onSync,
  onCommit,
  onDiscardAll,
  onDiscardFile,
  onUndeleteFile,
  isSelectedFileDeleted,
  onResolveAllLocal,
  onResolveAllRemote,
  onShowConflictView,
  onOpenSettings,
  chatMode,
}: LayoutProps & { chatMode: "sidebar" | "sheet" }) => {
  const isFileTreeOpen = useProjectUIStore((s) => s.isFileTreeOpen);
  const isChatOpen = useProjectUIStore((s) => s.isChatOpen);
  const selectedFilePath = useProjectUIStore((s) => s.selectedFilePath);
  const canSyncFromRemote = canProjectSyncFromRemote(project);
  const chatPanelRef = usePanelRef();
  const lastExpandedChatSizeRef = useRef(25);
  const isFileDirty =
    selectedFilePath !== null && dirtyPaths.includes(selectedFilePath);

  // Keep chat panel mounted when closed — resize to 0 instead of unmounting.
  // Avoid react-resizable-panels "collapsible" snapping behavior here; it made it
  // easy to get stuck with a ~0px panel that felt non-resizable.
  useEffect(() => {
    if (!chatPanelRef.current) return;
    const api = chatPanelRef.current;
    const currentPct = api.getSize()?.asPercentage ?? 0;

    if (isChatOpen) {
      const targetPct =
        currentPct > 1 ? currentPct : lastExpandedChatSizeRef.current;
      // `resize()` accepts strings like "30%" (recommended by the lib docs).
      api.resize(`${targetPct}%`);
      return;
    }

    if (currentPct > 1) {
      lastExpandedChatSizeRef.current = currentPct;
    }
    api.resize("0%");
  }, [isChatOpen, chatPanelRef]);

  const handleChatPanelResize = useCallback(
    (
      size: { asPercentage: number; inPixels: number },
      _id: string | number | undefined,
      _prevSize: unknown,
    ) => {
      // Remember the last non-trivial size so reopening restores it.
      if (size.asPercentage > 1) {
        lastExpandedChatSizeRef.current = size.asPercentage;
      }
    },
    [],
  );

  return (
    <div className="flex min-h-0 h-full flex-col">
      <ProjectPageHeader
        project={project}
        isDesktop={true}
        isSyncing={isSyncing}
        isCommitting={isCommitting}
        isDiscarding={isDiscarding}
        isResolvingConflicts={isResolvingConflicts}
        hasPendingChanges={hasPendingChanges}
        pendingChangeCount={pendingChangeCount}
        isOutOfDate={isOutOfDate}
        isConflicting={isConflicting}
        isIntegrationBlocked={isIntegrationBlocked}
        onCommit={onCommit}
        onDiscardAll={onDiscardAll}
        onOpenSettings={onOpenSettings}
      />
      <SyncBanner
        isSyncing={isSyncing}
        syncError={syncError}
        lockHint={lockHint}
        isWaitingOnLock={isWaitingOnLock}
        isOutOfDate={isOutOfDate}
        isConflicting={isConflicting}
        conflictCount={conflictCount}
        isChangeRequestPending={isChangeRequestPending}
        prUrl={prUrl}
        isIntegrationBlocked={isIntegrationBlocked}
        providerLabel={getProjectSourceLabel(project)}
        onSync={onSync}
        onResolveAllLocal={onResolveAllLocal}
        onResolveAllRemote={onResolveAllRemote}
        onReviewConflicts={onShowConflictView}
        isResolvingConflicts={isResolvingConflicts}
      />

      <ResizablePanelGroup orientation="horizontal" className="flex-1 min-h-0">
        {isFileTreeOpen && (
          <>
            <ResizablePanel
              id="file-tree"
              defaultSize="20"
              minSize="15"
              maxSize="35"
            >
              <FileTree
                nodes={fileTree}
                syncButton={
                  canSyncFromRemote
                    ? {
                        onSync,
                        isSyncing,
                        ariaLabel: `Sync from ${getProjectSourceLabel(project)}`,
                      }
                    : undefined
                }
              />
            </ResizablePanel>
            <ResizableHandle />
          </>
        )}

        <ResizablePanel id="editor" minSize="30">
          <div className="relative flex h-full flex-col ">
            {project.role === "viewer" && <ReadOnlyBanner />}

            <div className="flex justify-center">
              <Breadcrumbs
                projectName={project.name}
                filePath={selectedFilePath}
                isDirty={isFileDirty}
                isDeleted={isSelectedFileDeleted}
                fileTree={fileTree}
                onDiscardFile={onDiscardFile}
                onUndeleteFile={onUndeleteFile}
                rightSlot={<TableOfContentsButton />}
              />
            </div>
            <div className="flex-1 min-h-0">
              <EditorArea readOnly={project.role === "viewer"} />
            </div>
            <ChatBubble />
          </div>
        </ResizablePanel>

        {isAiChatEnabled && chatMode === "sidebar" && (
          <>
            <ResizableHandle />
            <ResizablePanel
              id="chat"
              panelRef={chatPanelRef}
              defaultSize="25"
              minSize="0"
              maxSize="40"
              onResize={handleChatPanelResize}
            >
              <ChatPanel projectId={projectId} />
            </ResizablePanel>
          </>
        )}
      </ResizablePanelGroup>

      {isAiChatEnabled && chatMode === "sheet" && (
        <Sheet
          open={isChatOpen}
          onOpenChange={(open) => {
            useProjectUIStore.getState().setChatOpen(open);
          }}
        >
          <SheetContent
            side="right"
            className="w-[420px] max-w-[80vw] sm:max-w-[80vw] p-0 gap-0"
            showCloseButton={false}
          >
            <SheetTitle className="sr-only">Chat</SheetTitle>
            <ChatPanel projectId={projectId} />
          </SheetContent>
        </Sheet>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Mobile layout — sheets for file tree & chat, floating chat bubble
// ---------------------------------------------------------------------------

const MobileLayout = ({
  token,
  projectId,
  project,
  fileTree,
  dirtyPaths,
  isSyncing,
  isCommitting,
  isDiscarding,
  isResolvingConflicts,
  hasPendingChanges,
  pendingChangeCount,
  syncError,
  lockHint,
  isWaitingOnLock,
  isOutOfDate,
  isConflicting,
  conflictCount,
  isChangeRequestPending,
  prUrl,
  isIntegrationBlocked,
  onSync,
  onCommit,
  onDiscardAll,
  onDiscardFile,
  onUndeleteFile,
  isSelectedFileDeleted,
  onResolveAllLocal,
  onResolveAllRemote,
  onShowConflictView,
  onOpenSettings,
}: LayoutProps) => {
  const setFileTreeOpenRaw = useProjectUIStore((s) => s.setFileTreeOpen);
  const setChatOpenRaw = useProjectUIStore((s) => s.setChatOpen);
  const selectedFilePath = useProjectUIStore((s) => s.selectedFilePath);
  const canSyncFromRemote = canProjectSyncFromRemote(project);
  const canPublish = canProjectPublish(project);
  const canDiscardToRemote = canProjectDiscardToRemote(project);
  const isFileDirty =
    selectedFilePath !== null && dirtyPaths.includes(selectedFilePath);
  const search = useSearch({ strict: false }) as ProjectSearch;
  const navigate = useNavigate();
  const { orgSlug, routeProjectId, baseTo, baseParams } =
    useProjectRouteState();

  const isFileTreeOpen = search.panel === "files";
  const isChatOpen = search.panel === "chat";
  const chatViewportHeight = useVisualViewportHeight(isChatOpen);

  // Keep the zustand store in sync for any shared UI that still reads it.
  useEffect(() => {
    setFileTreeOpenRaw(isFileTreeOpen);
    setChatOpenRaw(isChatOpen);
  }, [isChatOpen, isFileTreeOpen, setChatOpenRaw, setFileTreeOpenRaw]);

  const setPanel = useCallback(
    (panel: ProjectSearch["panel"], opts?: { replace?: boolean }) => {
      void navigate({
        to: baseTo,
        params: baseParams as any,
        search: (prev) => mergeProjectSearch(prev, { panel }),
        replace: opts?.replace,
      });
    },
    [baseParams, baseTo, navigate],
  );

  return (
    <div className="flex h-full flex-col">
      {/* Unified mobile header */}
      <ProjectPageHeader
        project={project}
        isDesktop={false}
        isSyncing={isSyncing}
        isCommitting={isCommitting}
        isDiscarding={isDiscarding}
        isResolvingConflicts={isResolvingConflicts}
        hasPendingChanges={hasPendingChanges}
        pendingChangeCount={pendingChangeCount}
        isOutOfDate={isOutOfDate}
        isConflicting={isConflicting}
        isIntegrationBlocked={isIntegrationBlocked}
        onCommit={onCommit}
        onDiscardAll={onDiscardAll}
        onOpenSettings={onOpenSettings}
      />

      {/* Dirty-state banner — slides in when there are pending changes */}
      <DirtyStateMobileBanner
        pendingChangeCount={pendingChangeCount}
        hasPendingChanges={hasPendingChanges}
        isCommitting={isCommitting}
        isDiscarding={isDiscarding}
        isSyncing={isSyncing}
        isOutOfDate={isOutOfDate}
        isConflicting={isConflicting}
        isIntegrationBlocked={isIntegrationBlocked}
        isResolvingConflicts={isResolvingConflicts}
        canPublish={canPublish}
        canDiscardToRemote={canDiscardToRemote}
        providerLabel={getProjectSourceLabel(project)}
        onCommit={onCommit}
        onDiscardAll={onDiscardAll}
      />

      <SyncBanner
        isSyncing={isSyncing}
        syncError={syncError}
        lockHint={lockHint}
        isWaitingOnLock={isWaitingOnLock}
        isOutOfDate={isOutOfDate}
        isConflicting={isConflicting}
        conflictCount={conflictCount}
        isChangeRequestPending={isChangeRequestPending}
        prUrl={prUrl}
        isIntegrationBlocked={isIntegrationBlocked}
        providerLabel={getProjectSourceLabel(project)}
        onSync={onSync}
        onResolveAllLocal={onResolveAllLocal}
        onResolveAllRemote={onResolveAllRemote}
        onReviewConflicts={onShowConflictView}
        isResolvingConflicts={isResolvingConflicts}
      />

      {/* Read-only banner for viewers */}
      {project.role === "viewer" && <ReadOnlyBanner />}

      {/* Breadcrumbs */}
      <Breadcrumbs
        projectName={project.name}
        filePath={selectedFilePath}
        isDirty={isFileDirty}
        isDeleted={isSelectedFileDeleted}
        fileTree={fileTree}
        onDiscardFile={onDiscardFile}
        onUndeleteFile={onUndeleteFile}
        rightSlot={<TableOfContentsButton />}
      />

      {/* Editor with floating chat bubble */}
      <div className="relative flex-1 overflow-hidden">
        <EditorArea readOnly={project.role === "viewer"} fileTree={fileTree} />
        <ChatBubble />
      </div>

      {/* Sheet overlays */}
      <Sheet
        open={isFileTreeOpen}
        onOpenChange={(open) => {
          if (open) setPanel("files");
          else if (search.panel === "files")
            setPanel(undefined, { replace: true });
        }}
      >
        <SheetContent side="left" className="w-72 p-0">
          <SheetHeader className="px-3 pt-3 pb-0">
            <SheetTitle className="text-sm">Files</SheetTitle>
          </SheetHeader>
          <div className="h-[calc(100%-48px)]">
            <FileTree
              nodes={fileTree}
              syncButton={
                canSyncFromRemote
                  ? {
                      onSync,
                      isSyncing,
                      ariaLabel: `Sync from ${getProjectSourceLabel(project)}`,
                    }
                  : undefined
              }
            />
          </div>
        </SheetContent>
      </Sheet>

      {/* Chat drawer — always mounted to preserve state (conversation, input, etc.) */}
      {isAiChatEnabled && (
        <div
          className="fixed inset-y-0 right-0 z-50 flex w-full max-w-full border-l bg-background shadow-lg transition-transform duration-300 ease-in-out sm:max-w-full"
          style={{
            transform: isChatOpen ? "translateX(0)" : "translateX(100%)",
            height:
              chatViewportHeight !== undefined
                ? `${chatViewportHeight}px`
                : "100dvh",
            pointerEvents: isChatOpen ? "auto" : "none",
          }}
          aria-hidden={!isChatOpen}
        >
          <ChatPanel
            projectId={projectId}
            onRequestClose={() => setPanel(undefined, { replace: true })}
          />
        </div>
      )}
      {/* Backdrop — only interactive when chat open */}
      {isAiChatEnabled && (
        <div
          className="fixed inset-0 z-40 bg-black/50 transition-opacity duration-300"
          style={{
            opacity: isChatOpen ? 1 : 0,
            pointerEvents: isChatOpen ? "auto" : "none",
          }}
          aria-hidden={!isChatOpen}
          onClick={() => {
            if (isChatOpen && search.panel === "chat") {
              setPanel(undefined, { replace: true });
            }
          }}
        />
      )}
    </div>
  );
};
